import React, { ChangeEvent, useCallback, useMemo, useRef } from 'react'
import { CardElement, PaymentRequestButtonElement } from '@stripe/react-stripe-js'
import classNames from 'classnames'
import { Else, If, Then, When } from 'react-if'
import { Button } from '@/atoms/Button'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { PayItForwardOutlineIcon } from '@/atoms/Icons/PayItForwardOutlineIcon'
import { Input } from '@/atoms/Input'
import { InternalLink } from '@/atoms/InternalLink'
import { CaptionSM, EyebrowMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { useElementOutOfView } from '@/hooks/useElementOutOfView'
import { PayPalPayment } from '@/molecules/PayPalPayment'
import { PaymentInfo } from '@/services/PifCheckoutService'
import { Offer } from '@/services/PifService'
import { isSupportedPayPalCurrency, isSupportedStripeCurrency } from '@/services/PifService/PifService'
import { formatPrice, getPriceForCurrency } from '@/services/PriceFormatter'
import { getDecimalsForCurrency } from '@/services/PriceFormatter/PriceFormatter'
import { useUser } from '@/services/UserService'
import { isValidEmail, isValidName } from '@/utils/StripeFormValidator'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { PrefilledCreditCardInput } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/PrefilledCreditCardInput'
import { useAppleGooglePay } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/useAppleGooglePay'
import { usePayItForwardForm } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/usePayItForwardForm'

export interface DavidStripeFormProps {
  offer: Offer
  clientSecret: string
}

export const DavidStripeForm: React.FC<DavidStripeFormProps> = ({ offer, clientSecret }) => {
  const { user } = useUser()
  const formStartRef = useRef<HTMLDivElement>(null)

  // Use existing hook to detect when placeholder is out of view
  // When it's out of view, button should be fixed. When in view, button is in natural position
  const [placeholderRef, isPlaceholderOutOfView] = useElementOutOfView<HTMLDivElement>()

  const {
    name,
    setName,
    email,
    setEmail,
    errorMessage,
    isSubmitting,
    savedPaymentInfo,
    setSavedPaymentInfo,
    checkout,
    onPayItForwardComplete,
  } = usePayItForwardForm()

  const {
    isSubmittingWithWallet,
    errorMessageWithWallet,
    paymentRequest: walletPaymentRequest,
  } = useAppleGooglePay({
    clientSecret,
    onComplete: onPayItForwardComplete,
    offer,
    isGuest: !user,
  })

  const { t } = useTranslate('pif')

  const nameFieldLabel = t('nameOnCard', 'Name on card')
  const emailFieldLabel = t('emailAddress', 'Email')
  const amount = formatPrice(offer.price, { currency: offer.currency })
  const amountToPayOneTime = t('amountToPayOneTime', 'Pay {{amount}}', {
    amount,
  })

  const isPayPalSupported = useMemo(() => isSupportedPayPalCurrency(offer.currency), [offer.currency])
  const isStripeSupported = useMemo(() => isSupportedStripeCurrency(offer.currency), [offer.currency])
  const displayOrSection = !!walletPaymentRequest || !!isPayPalSupported
  const isProcessing = isSubmitting || isSubmittingWithWallet

  const isButtonFixed = isPlaceholderOutOfView

  const isFormFilled = isValidName(name) && isValidEmail(email)

  const handleButtonClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isButtonFixed && !isFormFilled) {
        e.preventDefault()
        formStartRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        return
      }

      formStartRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    [isButtonFixed, isFormFilled],
  )

  return (
    <form onSubmit={checkout}>
      {isPayPalSupported && (
        <PayPalPayment
          name={slugs.david}
          onComplete={onPayItForwardComplete}
          description={`Angel: ${slugs.david}:pif`}
          softDescriptor={`${slugs.david || 'Pay it Forward'}`}
          sku={offer?.slug || name}
          currency={offer.currency.toUpperCase()}
          amount={getPriceForCurrency(offer.price, offer.currency).toFixed(getDecimalsForCurrency(offer.currency))}
          buttonHeight={48}
          isGuest={!user}
          paypalStyleOverride={{
            layout: 'vertical',
            color: 'white',
            shape: 'rect',
            height: 48,
          }}
          venmoStyleOverride={{
            layout: 'vertical',
            color: 'blue',
            shape: 'rect',
            height: 48,
          }}
        />
      )}
      {walletPaymentRequest && isStripeSupported && (
        <div className="h-50 mb-[7px] mt-1 rounded">
          <PaymentRequestButtonElement
            options={{
              paymentRequest: walletPaymentRequest,
              style: { paymentRequestButton: { height: '48px' } },
            }}
          />
        </div>
      )}

      {displayOrSection && (
        <div className="flex h-6 flex-row items-center py-4">
          <div className="h-[1px] grow-1 bg-core-gray-700" />
          <EyebrowMD color="core-gray-700" className="px-3 uppercase">
            <Translate t={t} i18nKey="or">
              or
            </Translate>
          </EyebrowMD>
          <div className="h-[1px] grow-1 bg-core-gray-700" />
        </div>
      )}

      <When condition={isSupportedStripeCurrency(offer.currency)}>
        <div ref={formStartRef}>
          <Input
            required
            usePlaceholderAsLabel
            value={name}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            label={nameFieldLabel}
            isValid={isValidName(name)}
            className={
              'photon-caption-md mt-1 h-12 rounded-[.625rem] !border-[1.5px] !border-core-gray-300 bg-white px-[.9375rem] py-4 text-core-gray-600 placeholder:lowercase placeholder:text-core-gray-500'
            }
            labelClassName={'photon-caption-md text-core-gray-700'}
            containerClassName="!mt-0"
            placeholder={nameFieldLabel}
          />
        </div>
        <div>
          <Input
            required
            usePlaceholderAsLabel
            value={email}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            label={emailFieldLabel}
            isValid={isValidEmail(email)}
            className={
              'photon-caption-md mt-1 h-12 rounded-[.625rem] !border-[1.5px] !border-core-gray-300 bg-white px-[.9375rem] py-4 text-core-gray-600 placeholder:lowercase placeholder:text-core-gray-500'
            }
            placeholder={t('emailAddressPlaceholder', 'Email Address')}
            labelClassName={'photon-caption-md text-core-gray-700'}
          />
        </div>
        <div className="w-full py-2">
          <If condition={!!savedPaymentInfo}>
            <Then>
              <div className="photon-caption-md text-core-gray-700">Card</div>
              <PrefilledCreditCardInput
                className="!h-12 !rounded-[.625rem] border !border-core-gray-200 !bg-white !px-4"
                savedPayment={savedPaymentInfo as PaymentInfo}
                changeCard={() => setSavedPaymentInfo(undefined)}
              />
            </Then>
            <Else>
              <div className="photon-caption-md text-core-gray-700">Card</div>
              <div className="photon-caption-md mt-1 h-12 rounded-[.625rem] !border-[1.5px] !border-core-gray-300 bg-white px-4 py-2.5 text-core-gray-600">
                <CardElement options={options} />
              </div>
            </Else>
          </If>
        </div>
        <CaptionSM color="core-gray-600" className="mt-2 text-[.625rem] !leading-[.875rem]">
          <Translate t={t} i18nKey="davidPifLegalTerms">
            Upon submitting my Pay it Forward payment, I acknowledge and agree that Angel becomes the owner of all funds
            upon receipt. Your Pay It Forward purchase, like other entertainment purchases, is meant to increase the
            audience and reach of the movie DAVID; it is not a donation.{' '}
            <InternalLink className="underline" href={`${paths.legal.termsOfUse}#pay-it-forward`}>
              Terms and Conditions
            </InternalLink>
            .
          </Translate>
        </CaptionSM>
        {/* Placeholder to maintain space when button is fixed */}
        <div ref={placeholderRef} className={isButtonFixed ? 'h-[120px] md:h-[140px]' : ''} />

        <div
          className={classNames(
            'w-full shrink-0',
            isButtonFixed
              ? 'fixed inset-x-0 bottom-0 z-10 flex border-t border-[#D9D1C9] bg-[#FCF4ED] p-4 md:!p-6'
              : 'mt-8 md:mt-10',
          )}
        >
          <Button
            aria-label="pay-button"
            type="submit"
            variant="plain"
            size="md"
            disabled={isProcessing}
            onClick={handleButtonClick}
            className={classNames(
              'flex w-full !py-[.875rem] !rounded-xl !px-[1.25rem] items-center justify-between gap-6 bg-david-orange border-david-orange text-white hover:border-core-warning-700 hover:bg-core-warning-700 active:border-core-warning-700 active:bg-core-warning-700',
            )}
          >
            <span className="font-angel-royal flex items-center gap-1.5 text-[1.625rem] !lowercase not-italic leading-[1.5rem] text-white">
              {isProcessing ? (
                <LoadingSpinnerIcon size={24} color="white" />
              ) : (
                <PayItForwardOutlineIcon className="shrink-0" color="white" size={24} />
              )}
              {isProcessing ? t('stripeProcessing', 'Processing ...') : t('payItForward', 'Pay it Forward')}
            </span>
            <span className="font-angel-royal flex items-center text-[1.625rem] !lowercase not-italic leading-[1.5rem] text-white">
              {amountToPayOneTime}
              <CaretRightIcon color="white" size={24} />
            </span>
          </Button>
        </div>
      </When>
      <div className="-mb-4 pt-4 text-center text-red">
        {errorMessage ? errorMessage : errorMessageWithWallet ? errorMessageWithWallet : ''}
      </div>
    </form>
  )
}

const options = {
  style: {
    base: {
      color: '#7B7979',
      colorText: '#7B7979',
      backgroundColor: '#FFF',
      borderRadius: '10px',
      height: '48px',
      fontSize: '16px',
      fontFamily: 'inherit',
      lineHeight: '28px',
      '::placeholder': {
        color: '#9D9C9B',
        colorText: '#7B7979',
        fontSize: '14px',
        fontFamily: 'inherit',
      },
    },
  },
}
